/**
 * ComposerPrimaryAction — the composer's primary button: send, or stop while a
 * response is streaming.
 *
 * Live used to share this slot, appearing as a sparkle whenever the field was
 * empty. It now has its own labelled control beside this one
 * (`ComposerLiveButton`), so this button has exactly one job in every state and
 * the primary action never changes meaning under the user's cursor.
 *
 * Extracted from ChatComposer because that component is already well past its
 * complexity budget.
 */

import { IconButton } from '@chakra-ui/react';

import { MaterialIcon } from '../../ui/MaterialIcon';

interface ComposerPrimaryActionProps {
  readonly isStreaming: boolean;
  /**
   * A turn this tab already asked to stop is still unwinding. Send stays send,
   * and disabled: there is nothing more to stop. (A turn running on another
   * request comes in as `isStreaming`, since Stop ends it on the server.)
   */
  readonly isWaiting?: boolean;
  readonly canSend: boolean;
  readonly sendLabel: string;
  readonly onCancel?: () => void;
  readonly onSend: () => void;
}

export function ComposerPrimaryAction({
  isStreaming,
  isWaiting = false,
  canSend,
  sendLabel,
  onCancel,
  onSend,
}: ComposerPrimaryActionProps) {
  return (
    <IconButton
      aria-label={isStreaming ? 'Stop response' : sendLabel}
      // `arrow_upward`, not the paper plane: the 2026-09 composer draws send as
      // an arrow-up in a circle.
      icon={isStreaming ? <MaterialIcon name="stop" /> : <MaterialIcon name="arrow_upward" />}
      onClick={isStreaming ? onCancel : onSend}
      size="md"
      minW="38px"
      h="38px"
      bg={isStreaming ? 'danger.500' : 'brand.primary'}
      color={isStreaming ? 'white' : 'text.onBrand'}
      _hover={{
        bg: isStreaming ? 'danger.600' : 'brand.hover',
        transform: isStreaming || (canSend && !isWaiting) ? 'scale(1.05)' : 'none',
      }}
      // Visibly disabled until there's content — the state is the affordance,
      // not a surprise on click. Twilight 200 per the 2026-09 composer: it
      // reads as the send button waiting rather than as a different control.
      //
      // Deliberately pale, and allowed to be: WCAG 1.4.3/1.4.11 exempt
      // components that are INACTIVE, so a disabled control is the one place
      // the brand's light end is safe. Re-check if it ever becomes clickable.
      _disabled={{
        bg: 'accent.200',
        color: 'text.onBrand',
        cursor: 'not-allowed',
        opacity: 1,
      }}
      borderRadius="full"
      isDisabled={!isStreaming && (isWaiting || !canSend)}
      transition="all 0.2s ease"
    />
  );
}
